/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  GroupEntity,
  RELATION_CHILD_OF,
  RELATION_PARENT_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  Avatar,
  InfoCard,
  InfoCardVariants,
  Link,
} from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import {
  EntityRefLinks,
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useCallback } from 'react';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import AccountTreeIcon from '@material-ui/icons/AccountTree';
import Alert from '@material-ui/lab/Alert';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import GroupIcon from '@material-ui/icons/Group';
import PermIdentityIcon from '@material-ui/icons/PermIdentity';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { LinksGroup } from './LinksGroup';
import ManagerLightIcon from './manager_light.svg';
import ManagerDarkIcon from './manager_dark.svg';
import { useTheme } from '@material-ui/core/styles';

const CardTitle = (props: { title: string }) => (
  <Box display="flex" alignItems="center">
    <GroupIcon fontSize="inherit" />
    <Box ml={1}>{props.title}</Box>
  </Box>
);

/** @public */
export const GroupProfileCard = (props: {
  variant?: InfoCardVariants;
  showLinks?: boolean;
}) => {
  // Theme-aware icon switching using <img> instead of React SVG components, to support a custom manager icon without build config changes
  const theme = useTheme();
  const managerIcon = theme.palette.type === 'dark' ? ManagerDarkIcon : ManagerLightIcon;

  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  type CustomGroupEntity = GroupEntity & {
    spec: GroupEntity['spec'] & {
      manager?: string;
    };
  };

  const { entity: group } = useEntity<CustomGroupEntity>();
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );
  const t = (key: string) => key;

  const refreshEntity = useCallback(async () => {
    await catalogApi.refreshEntity(stringifyEntityRef(group));
    alertApi.post({
      message: 'Refresh scheduled',
      severity: 'info',
      display: 'transient',
    });
  }, [catalogApi, alertApi, group]);

  if (!group) {
    return (
      <Alert severity="error">{t('groupProfileCard.groupNotFound')}</Alert>
    );
  }

  const {
    metadata: { name, description, title, annotations, links },
    spec: { profile },
  } = group;

  const childRelations = getEntityRelations(group, RELATION_PARENT_OF, {
    kind: 'Group',
  });
  const parentRelations = getEntityRelations(group, RELATION_CHILD_OF, {
    kind: 'group',
  });

  const entityLocation = annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const entityMetadataEditUrl =
    group.metadata.annotations?.[ANNOTATION_EDIT_URL];

  const displayName = profile?.displayName ?? title ?? name;
  const emailHref = profile?.email ? `mailto:${profile.email}` : '#';
  const infoCardAction = entityMetadataEditUrl ? (
    <IconButton
      aria-label={t('groupProfileCard.editIconButtonTitle')}
      title={t('groupProfileCard.editIconButtonTitle')}
      component={Link}
      to={entityMetadataEditUrl}
    >
      <EditIcon />
    </IconButton>
  ) : (
    <IconButton
      aria-label={t('groupProfileCard.editIconButtonTitle')}
      disabled
      title={t('groupProfileCard.editIconButtonTitle')}
    >
      <EditIcon />
    </IconButton>
  );

  return (
    <InfoCard
      title={<CardTitle title={displayName} />}
      subheader={description}
      variant={props.variant}
      action={
        <>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label={t('groupProfileCard.refreshIconButtonAriaLabel')}
              title={t('groupProfileCard.refreshIconButtonTitle')}
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          {infoCardAction}
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={2} xl={1}>
          <Avatar displayName={displayName} picture={profile?.picture} />
        </Grid>
        <Grid item md={10} xl={11}>
          <List>
            <ListItem>
              <ListItemIcon>
                <Tooltip title={t('entityRef')}>
                  <PermIdentityIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                primary={stringifyEntityRef(group)}
                secondary={t('Entity Reference')}
              />
            </ListItem>
            {profile?.email && (
              <ListItem>
                <ListItemIcon>
                  <Tooltip title={t('Email')}>
                    <EmailIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={<Link to={emailHref}>{profile.email}</Link>}
                  secondary={t('Email')}
                />
              </ListItem>
            )}
            {group.spec?.manager && (
              <ListItem>
                <ListItemIcon>
                  <Tooltip title="Manager">
                    <img
                      src={managerIcon}
                      alt="Manager"
                      style={{ width: 30, height: 30 }}
                    />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link
                      to={`/catalog/default/user/${group.spec.manager
                        .split(':')
                        .pop()}`}
                    >
                      {group.spec.manager
                        .split(':')
                        .pop()
                        ?.split(/[._-]/)
                        .map(w => w[0]?.toUpperCase() + w.slice(1))
                        .join(' ')}
                    </Link>
                  }
                  secondary="Manager"
                />
              </ListItem>
            )}
            <ListItem>
              <ListItemIcon>
                <Tooltip title={t('parentGroup')}>
                  <AccountTreeIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                primary={
                  parentRelations.length ? (
                    <EntityRefLinks
                      entityRefs={parentRelations}
                      defaultKind="Group"
                    />
                  ) : (
                    'N/A'
                  )
                }
                secondary={t('Parent Group')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Tooltip title={t('childGroups')}>
                  <GroupIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                primary={
                  childRelations.length ? (
                    <EntityRefLinks
                      entityRefs={childRelations}
                      defaultKind="Group"
                    />
                  ) : (
                    'N/A'
                  )
                }
                secondary={t('Child Groups')}
              />
            </ListItem>
            {props?.showLinks && <LinksGroup links={links} />}
          </List>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
